import { Trans, useLingui } from "@lingui/react/macro";
import { patientKeys, updateLifeCare } from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import Checkbox from "@/components/Checkbox/Checkbox";

export const EditLifeCare = ({
  currentLifeCare,
  onSuccess,
  patientId,
}: {
  currentLifeCare: boolean | null | undefined;
  onSuccess: () => void;
  patientId: string;
}) => {
  const { t } = useLingui();
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      lifeCare: currentLifeCare,
    },
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (lifeCare: boolean | null | undefined) =>
      updateLifeCare(patientId, lifeCare),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        mutate(formData.lifeCare);
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar LifeCare`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : undefined}
          <Form.Row>
            <Checkbox label={{ text: t`LifeCare` }} {...register("lifeCare")} />
          </Form.Row>
          <FilledButton type="submit">
            <Trans>Spara ändringar</Trans>
          </FilledButton>
        </>
      )}
    </Form>
  );
};
